/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import { Observable } from 'rxjs';
import { Point, ElementRect, ISkinInformation } from '../../remote-control-ng';
import { Transformation, Rect, NxtRemoteControlPositioningService } from './nxt-remote-control-positioning.service';
import { DeviceScreenRenderingService } from '../../remote-control-ng/services/deviceScreenRenderingService';
import { MathHelpers } from '../../remote-control-ng/utils/math';
import { RcContext } from '../../remote-control-ng/context/rcContext';
import { VideoRecordingSetupMessage } from '../../remote-control-ng/messages/xsight';
import { RcStore } from '../../remote-control-ng/services/rcStore';
import { Injectable } from '@angular/core';

export interface ButtonInfo {
    // compatibility --- keep the variable name so that can be called by mcrc.com.DeviceInput
    id: string;
    type: string;
    keyValue: number;
    area: ElementRect;
}

export interface SkinInformation {
    id: string;
    name: string;
    deviceName: string;
    rotation: number;
    screen: ElementRect;
    isIrregular: boolean;
    buttons: ButtonInfo[];
    // index: string[],
    imageWidth: number;
    imageHeight: number;
}

interface ServerSkinInformation {
    id: string;
    publicRelease: boolean;
    skinSz: number;
    pngSkinSz: number;
    name: string;
    deviceName: string;
    rotation: number;
    screen: string;
    hasNotchImage: boolean;
    buttons: any[];
    index: string[];
    imageWidth: number;
    imageHeight: number;
}

export interface Skin {
    /** Original skin, we got from server */
    original: {
        image: HTMLImageElement;
        information: SkinInformation;
    };
    transformed: {
        canvas: HTMLCanvasElement;
        information: SkinInformation;
        drawnDeviceRect: ElementRect
    };
}

export interface RenderCoordinates {
    skin: Rect;
    screen: Rect;
}

@Injectable({
    providedIn: 'root'
})
export class NxtRemoteControlSkinService {
    public renderCoordinates: RenderCoordinates;
    private readonly SkinCacheTimeoutMinutes = 60;

    constructor(
        private remoteControlPositioningService: NxtRemoteControlPositioningService,
        private localStorage: RcStore
    ) { }

    public loadSkin = (skinName: string): Observable<Skin> => {
        const result = new Observable<Skin>((subscriber) => {
            let skin: Skin;

            this.loadSkinInformation(skinName).subscribe(
                (serverSkinInformation: ServerSkinInformation) => {
                    if (serverSkinInformation?.screen) {
                        const skinInformation: SkinInformation = this.mapServerSkinInformation(serverSkinInformation);
                        this.loadSkinImage(skinInformation).subscribe((skinImage: HTMLImageElement) => {
                            const skin = this.createSkin(skinImage, skinInformation);
                            this.sendSkinInfo(skinInformation, serverSkinInformation);
                            subscriber.next(skin);
                            subscriber.complete();
                        });
                    } else {
                        skin = this.createEmptySkin(skinName);
                        subscriber.next(skin);
                        subscriber.complete();
                    }
                },
                error => {
                    subscriber.error(error);
                }
            );
        });

        return result;
    };

    public resetSkins(): void {
        RcContext.instance.session.setupRecordingOnConnect(new VideoRecordingSetupMessage(null));
    }

    private sendSkinInfo = (skinInfo: SkinInformation, serverSkinInfo: ServerSkinInformation): void => {
        let videoSkinData = {
            id: skinInfo.id,
            deviceWidth: skinInfo.imageWidth,
            deviceHeight: skinInfo.imageHeight,
            hasNotchImage: serverSkinInfo.hasNotchImage,
            screen: skinInfo.screen,
            rotation: skinInfo.rotation,
        } as ISkinInformation;

        RcContext.instance.session.getConnection().setRecordingSkinConfiguration(
            new VideoRecordingSetupMessage(videoSkinData)
        );
    }

    /**
     * Updates the skin scale/rotation/render coordinates if needed
     * Returns the coordinates where the screen should be rendered
     * */
    public renderSkinIfNeeded(skin: Skin, rotation: number, canvas: HTMLCanvasElement, deviceScreen: Rect, autoZoom: boolean, offset: Point): boolean {
        this.adjustCanvas(canvas);

        /*
        // if the skin does not match the required rotation or canvas dimensions...
        if (this.transformSkinToMatchDevice(skin, rotation, canvas.width, canvas.height, deviceScreen.width, deviceScreen.height, autoZoom)) {
            this.renderCoordinates = this.calculateRenderCoordinates(skin, canvas);
            this.renderSkinOnCanvas(skin, canvas, this.renderCoordinates);
            return true;
        }

        // No rendering needed
        return false;
        //*/

        // seems like we always need to render skin and reposition device screen when this function is called
        const transformation = this.transformSkinToMatchDevice(skin, rotation, canvas.width, canvas.height, deviceScreen.width, deviceScreen.height, autoZoom);
        this.remoteControlPositioningService.scaleOffset(transformation, canvas, this.skinToRect(skin) || deviceScreen, offset);
        this.renderCoordinates = this.calculateRenderCoordinates(skin, canvas);
        this.renderSkinOnCanvas(skin, canvas, this.renderCoordinates, offset.x, offset.y);
        skin.transformed.drawnDeviceRect = DeviceScreenRenderingService.getDrawnDeviceRect(skin.transformed.canvas);
        this.renderSkinOnCanvas(skin, canvas, this.renderCoordinates, offset.x, offset.y);
        return true;
    }

    /* gets the Rect object from a current transformed skin or null when not initialized */
    private skinToRect = (skin: Skin): Rect => {
        if (!skin.transformed.information) return null;
        return {
            x: 0,
            y: 0,
            height: skin.transformed.information.imageHeight,
            width: skin.transformed.information.imageWidth
        } as Rect;
    }

    public getSkinScale = (skin: Skin): number => {
        const rotation = this.getRelativeRotation(skin);
        const scaledWidth = rotation % 2 == 0 ? skin.transformed.information.imageWidth : skin.transformed.information.imageHeight;
        //const scaledHeight = rotation % 2 == 0 ? skin.transformed.information.imageHeight : skin.transformed.information.imageWidth;
        return scaledWidth * 1.0 / skin.original.information.imageWidth;
    };

    /** Calculates where in the canvas should the skin and the screen be rendered, and returns canvas compatible values (x, y, width, height) */
    private calculateRenderCoordinates = (skin: Skin, canvas: HTMLCanvasElement): RenderCoordinates => {

        const skinCoordinates = {
            x: (canvas.width - skin.transformed.information.imageWidth) / 2,
            y: (canvas.height - skin.transformed.information.imageHeight) / 2,
            width: skin.transformed.information.imageWidth,
            height: skin.transformed.information.imageHeight
        };

        return {
            skin: skinCoordinates,
            screen: {
                x: skinCoordinates.x + skin.transformed.information.screen.left,
                y: skinCoordinates.y + skin.transformed.information.screen.top,
                width: skin.transformed.information.screen.right - skin.transformed.information.screen.left,
                height: skin.transformed.information.screen.bottom - skin.transformed.information.screen.top,
            }
        };
    };

    private renderSkinOnCanvas = (skin: Skin, canvas: HTMLCanvasElement, renderCoordinates: RenderCoordinates, offsetX = 0, offsetY = 0): void => {
        const canvasContext = canvas.getContext('2d');
        canvasContext.clearRect(0, 0, canvas.width, canvas.height);

        // Draw the skin to screen
        canvasContext.drawImage(skin.transformed.canvas,
            renderCoordinates.skin.x + offsetX,
            renderCoordinates.skin.y + offsetY,
            renderCoordinates.skin.width,
            renderCoordinates.skin.height);
    };

    private adjustCanvas = (canvas: HTMLCanvasElement): void => {
        if (canvas.width != canvas.clientWidth) {
            canvas.width = canvas.clientWidth;
        }
        if (canvas.height != canvas.clientHeight) {
            canvas.height = canvas.clientHeight;
        }
    };

    private createSkin = (skinImage: HTMLImageElement, skinInformation: SkinInformation): Skin => {
        if (skinImage) {
            if (!skinInformation.isIrregular) {
                const canvas = DeviceScreenRenderingService.convertColorToAlpha(skinImage);
                skinImage.src = canvas.toDataURL();
            }

            const skinCanvas = document.createElement('canvas');
            skinCanvas.width = skinImage.naturalWidth;
            skinCanvas.height = skinImage.naturalHeight;

            const ctx = skinCanvas.getContext('2d');
            ctx.drawImage(skinImage, 0, 0);

            const skin: Skin = {
                original: {
                    image: skinImage,
                    information: skinInformation
                },
                transformed: {
                    canvas: skinCanvas,
                    information: this.copySkinInformation(skinInformation),
                    drawnDeviceRect: null
                }
            };

            return skin;
        }

        throw new Error('missing skin code not implemented');
    };

    private transformSkinToMatchDevice = (skin: Skin, rotation: number, canvasWidth: number, canvasHeight: number, screenWidth: number, screenHeight: number, autoScale: boolean): Transformation => {
        // Calculate the scale we need to use to transform the skins
        const relativeRotation = this.getRelativeRotation(skin, rotation);
        let skinScale = 1;   // If not autoscaling, then scale remains unchanged

        // 2 types of skin scaling calculation differ slightly even for identity zooming
        // * identity zoom - scale skin to fit canvas (canvasWidth/canvasHeight)
        // * custom zoom - scale skin to fit device screen (screenWidth/screenHeight)

        // always calculate what the identity zooming would be so that we don't
        // have small jumps between to custom 100% and identity zoom
        const skinWidth = (relativeRotation % 2) === 0 ? skin.original.information.imageWidth : skin.original.information.imageHeight;
        const skinHeight = (relativeRotation % 2) === 0 ? skin.original.information.imageHeight : skin.original.information.imageWidth;
        // pad canvas on each side when calculation the initial scale
        // 5px is less than what the Old RC does, similar to it but maintinaing more real estate
        const whiteSpace = Math.max(Math.min(canvasHeight, canvasWidth) / 50, 10);
        const scaleX = (canvasWidth - whiteSpace * 2) / skinWidth;
        const scaleY = (canvasHeight - whiteSpace * 2) / skinHeight;
        skinScale = Math.min(scaleX, scaleY);
        if (!autoScale) {
            // what would be a scale skin to fit device screen (screenWidth/screenHeight)
            const width = skin.original.information.screen.right - skin.original.information.screen.left;
            const height = skin.original.information.screen.bottom - skin.original.information.screen.top;
            const skinScreenWidth = (relativeRotation % 2) === 0 ? width : height;
            const skinScreenHeight = (relativeRotation % 2) === 0 ? height : width;
            const scaleX2 = screenWidth / skinScreenWidth;
            const scaleY2 = screenHeight / skinScreenHeight;
            const customSkinScale = Math.min(scaleX2, scaleY2);
            // if custom scale is less than auto scale and within 5%, use identity scale
            if (customSkinScale > skinScale || (skinScale - customSkinScale) / skinScale > 0.05) {
                skinScale = customSkinScale;
            }
        }

        const currentScale = this.getSkinScale(skin);

        if (skin.transformed.information.rotation !== rotation || skinScale !== currentScale) {
            console.log(`[SkinService]: rotation: ${skin.transformed.information.rotation} => ${rotation} skinScale: ${currentScale} => ${skinScale}`);
        }

        this.transformSkinInformation(skin, rotation, skinScale);

        // At this point skin.transformed.information contains the skin details as we need them to render the next frame,
        // now we need to transform the skin image to make it match this information

        this.transformSkinImageToMatchSkinInformation(skin);
        const result: Transformation = {
            nextScale: skinScale,
            previousScale: currentScale,
            rotation: rotation - relativeRotation,
        };

        return result;
    };

    private transformSkinInformation = (skin: Skin, rotation: number, skinScale: number): void => {
        // Start from the original skin information
        skin.transformed.information = this.copySkinInformation(skin.original.information);
        const relativeRotation = this.getRelativeRotation(skin, rotation);

        // Rotate the screen area
        MathHelpers.applyRotation(
            skin.transformed.information.screen,
            skin.transformed.information.imageWidth,
            skin.transformed.information.imageHeight,
            relativeRotation);

        // rotate button areas
        skin.transformed.information.buttons.forEach(item => MathHelpers.applyRotation(item.area, skin.transformed.information.imageWidth, skin.transformed.information.imageHeight, relativeRotation));

        // Rotate the skin dimensions
        if (relativeRotation === 1 || relativeRotation === 3) {
            const tempWidth = skin.transformed.information.imageWidth;
            skin.transformed.information.imageWidth = skin.transformed.information.imageHeight;
            skin.transformed.information.imageHeight = tempWidth;
        }

        // update the current transformed rotation
        skin.transformed.information.rotation = rotation;

        // If there is a change in the scale...
        if (skinScale !== 1) {
            skin.transformed.information.imageWidth = skin.transformed.information.imageWidth * skinScale;
            skin.transformed.information.imageHeight = skin.transformed.information.imageHeight * skinScale;

            // XTODO:
            skin.transformed.information.screen.top = skin.transformed.information.screen.top * skinScale;
            skin.transformed.information.screen.left = skin.transformed.information.screen.left * skinScale;
            skin.transformed.information.screen.bottom = skin.transformed.information.screen.bottom * skinScale;
            skin.transformed.information.screen.right = skin.transformed.information.screen.right * skinScale;

            // scale button areas
            skin.transformed.information.buttons.forEach(item => {
                item.area.top *= skinScale;
                item.area.left *= skinScale;
                item.area.bottom *= skinScale;
                item.area.right *= skinScale;
            });
        }
    };

    private transformSkinImageToMatchSkinInformation = (skin: Skin): void => {
        const skinCanvas = skin.transformed.canvas;
        const skinImage = skin.original.image;
        const rotation = this.getRelativeRotation(skin);
        const degrees = -1 * 90 * rotation;

        // Configure the canvas to the dimensions we have in transformed.information
        skinCanvas.width = skin.transformed.information.imageWidth;
        skinCanvas.height = skin.transformed.information.imageHeight;

        console.log('original skin:', skinImage.width, 'x', skinImage.height);
        console.log('transformed skin:', skinCanvas.width, 'x', skinCanvas.height, rotation);

        const skinCanvasCtx = skinCanvas.getContext('2d');

        skinCanvasCtx.save();
        // set the frame of reference to the point in the center of the canvas...
        skinCanvasCtx.translate(skinCanvas.width / 2, skinCanvas.height / 2);
        // rotate the frame of reference as the rotation requires
        skinCanvasCtx.rotate(degrees * Math.PI / 180);

        if (rotation === 0 || rotation == 2) {
            skinCanvasCtx.drawImage(skinImage,
                0,
                0,
                skinImage.width,
                skinImage.height,
                -skinCanvas.width / 2,
                -skinCanvas.height / 2,
                skinCanvas.width,
                skinCanvas.height);
        } else {
            skinCanvasCtx.drawImage(skinImage,
                0,
                0,
                skinImage.width,
                skinImage.height,
                -skinCanvas.height / 2,
                -skinCanvas.width / 2,
                skinCanvas.height,
                skinCanvas.width);
        }

        skinCanvasCtx.restore();
    };

    private mapServerSkinInformation = (json: ServerSkinInformation): SkinInformation => {
        // Find an example at: https://skinsapi.soti.net/api/skins/iPhone_X
        const screenInfo = json.screen.split(',');
        const top = Number(screenInfo[0]);
        const left = Number(screenInfo[1]);
        const bottom = Number(screenInfo[2]);
        const right = Number(screenInfo[3]);

        const buttons = json.buttons.map(item => {
            const area = item.area.split(',').map(i => Number(i));
            const buttonInfo: ButtonInfo = {
                id: item.id,
                area: {
                    x: 0,
                    y: 0,
                    left: area[0],
                    top: area[1],
                    right: area[2],
                    bottom: area[3],
                    width: area[2] - area[0],
                    height: area[3] - area[1],
                },
                type: item.key.substring(0, 2),
                keyValue: parseInt(item.key.substring(2)),
            };

            return buttonInfo;
        });

        const skinInfo: SkinInformation = {
            id: json.id,
            name: json.name,
            deviceName: json.deviceName,

            screen: {
                x: 0,
                y: 0,
                top: top,
                left: left,
                bottom: bottom,
                right: right,
                height: bottom - top,
                width: right - left,
            },
            imageWidth: json.imageWidth,
            imageHeight: json.imageHeight,
            rotation: json.rotation,
            isIrregular: json.hasNotchImage,
            buttons: buttons,
        };

        return skinInfo;
    };

    private createEmptySkin = (skinName: string): Skin => {
        return null;
    };

    private loadSkinInformation = (skinName: string): Observable<ServerSkinInformation> => {
        const result = new Observable<ServerSkinInformation>((subscriber) => {
            // try local storage first
            const store = this.localStorage;
            const localSkinInfo = store.loadString('info.' + skinName, this.SkinCacheTimeoutMinutes);
            if (localSkinInfo) {
                const skinInfo = JSON.parse(localSkinInfo);
                subscriber.next(skinInfo);
            } else {
                const xmlHttp = new XMLHttpRequest();
                xmlHttp.onreadystatechange = function () {
                    if (xmlHttp.status == 200 && xmlHttp.readyState == XMLHttpRequest.DONE) {
                        // if (xmlHttp.response === undefined) {
                        //     xmlHttp.response = xmlHttp.responseText;
                        // }

                        const skinInformation = xmlHttp.response;
                        // if (typeof xmlHttp.response != "object") {
                        //     json = JSON.parse(xmlHttp.response);
                        // }

                        if (!skinInformation.publicRelease) {
                            subscriber.error(skinName + ': skin not released');
                        } else {
                            // save to local storage
                            store.storeString('info.' + skinName, JSON.stringify(skinInformation));
                            subscriber.next(skinInformation);
                        }
                    } else if (xmlHttp.status == 0 && xmlHttp.readyState == XMLHttpRequest.DONE) {
                        subscriber.next();
                    }
                };

                if (xmlHttp.overrideMimeType !== undefined) {
                    xmlHttp.overrideMimeType('application/json');
                }

                console.log(`Load skin info: ${skinName}`);

                xmlHttp.open('GET', 'https://skinsapi.soti.net/api/skins/' + skinName + '/', true);
                xmlHttp.responseType = 'json';
                xmlHttp.setRequestHeader('If-Modified-Since', 'Sat, 1 Jan 2005 00:00:00 GMT');
                xmlHttp.setRequestHeader('Accept', 'text/html,application/json,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8');
                xmlHttp.setRequestHeader('Access-Control-Allow-Origin', '*');
                xmlHttp.send();
            }
        });

        return result;
    };

    private loadSkinImage = (skinInformation: SkinInformation): Observable<HTMLImageElement> => {
        const result = new Observable<HTMLImageElement>((observer) => {
            if (skinInformation.id) {
                // try local storage first
                const localSkinImage = this.localStorage.loadImage('image.' + this.getSkinImageName(skinInformation), this.SkinCacheTimeoutMinutes);

                const skinImage = new Image();
                skinImage.onload = (event: Event) => {
                    console.log(`Skin image loaded: ${skinInformation.id}: ${skinImage.naturalWidth} x ${skinImage.naturalHeight}`);
                    // save to local storage
                    if (!localSkinImage) {
                        this.localStorage.storeImage('image.' + this.getSkinImageName(skinInformation), skinImage);
                    }

                    observer.next(skinImage);
                    observer.complete();
                };

                skinImage.onerror = (error) => {
                    console.error('error loading skin image', error);
                    observer.error(error);
                };

                if (localSkinImage) {
                    skinImage.src = localSkinImage;
                } else {
                    skinImage.crossOrigin = 'anonymous';
                    skinImage.src = 'https://skinsapi.soti.net/images/skins/' + this.getSkinImageName(skinInformation) + '.png';
                }
            } else {
                observer.error('skin information does not contain a skin image id');
            }
        });
        return result;
    };

    private copySkinInformation = (skinInformation: SkinInformation): SkinInformation => (JSON.parse(JSON.stringify(skinInformation)));

    private getSkinImageName = (skinInformation: SkinInformation): string =>
        // Irregular skin devices, will have a different image, identified by the .T (of Transparent) sufix.
        skinInformation.isIrregular ? skinInformation.id + '.T' : skinInformation.id;


    private getRelativeRotation = (skin: Skin, rotation?: number): number => {
        if (rotation === undefined) {
            rotation = skin.transformed.information.rotation;
        }

        let relativeRotation = rotation - skin.original.information.rotation;
        if (relativeRotation < 0) {
            relativeRotation += 4;
        }

        return relativeRotation;
    };
}
